// src/components/AddFriendModal.jsx
import React, { useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import Modal from './Modal';
import QRCodeScanner from './QRCodeScanner';
import notify from '../lib/notify';
import { MAX_FRIENDS } from '../lib/config';
import { Button as FcButton, Input as FcInput } from '../ui';

export default function AddFriendModal({ isOpen, onClose, currentUserId, onSuccess }) {
  const [mode, setMode] = useState('input'); // 'input' or 'scan'
  const [friendId, setFriendId] = useState('');
  const [loading, setLoading] = useState(false);
  const [friendCount, setFriendCount] = useState(null);

  // Fetch friend count on mount
  React.useEffect(() => {
    if (isOpen && currentUserId) {
      fetchFriendCount();
    }
  }, [isOpen, currentUserId]);

  const fetchFriendCount = async () => {
    try {
      const { data, error } = await supabase
        .rpc('get_friend_count', { p_user_id: currentUserId });
      
      if (error) throw error;
      setFriendCount(data || 0);
    } catch (error) {
    }
  };

  const handleSendRequest = async (targetFriendId) => {
    if (!targetFriendId || targetFriendId.length !== 8) {
      notify('Please enter a valid 8-character Friend ID', 'error');
      return;
    }

    setLoading(true);
    try {
      // Check friend limit before proceeding
      if (friendCount !== null && friendCount >= MAX_FRIENDS) {
        notify(`You have reached the maximum of ${MAX_FRIENDS} friends`, 'error');
        setLoading(false);
        return;
      }

      // Find user by referral code (Friend ID)
      const { data: userData, error: userError } = await supabase
        .from('user_referral_codes')
        .select('user_id')
        .eq('referral_code', targetFriendId)
        .single();

      if (userError || !userData) {
        notify('Friend ID not found', 'error');
        setLoading(false);
        return;
      }

      const receiverId = userData.user_id;

      // Check if it's yourself
      if (receiverId === currentUserId) {
        notify('You cannot send a friend request to yourself', 'error');
        setLoading(false);
        return;
      }

      // Check if already friends (check both directions since user1_id < user2_id)
      const minId = currentUserId < receiverId ? currentUserId : receiverId;
      const maxId = currentUserId < receiverId ? receiverId : currentUserId;
      
      const { data: existingFriendship, error: friendshipError } = await supabase
        .from('friendships')
        .select('id')
        .eq('user1_id', minId)
        .eq('user2_id', maxId)
        .single();

      if (existingFriendship) {
        notify('You are already friends with this user', 'error');
        setLoading(false);
        return;
      }

      // Check for existing pending request (either direction)
      const { data: existingRequest, error: requestCheckError } = await supabase
        .from('friend_requests')
        .select('id, status')
        .or(`and(sender_id.eq.${currentUserId},receiver_id.eq.${receiverId}),and(sender_id.eq.${receiverId},receiver_id.eq.${currentUserId})`)
        .eq('status', 'pending')
        .maybeSingle();

      if (requestCheckError) {
        // Continue anyway, the database will prevent duplicates
      }

      if (existingRequest) {
        notify('A friend request is already pending', 'error');
        setLoading(false);
        return;
      }

      // Send friend request
      const { error: insertError } = await supabase
        .from('friend_requests')
        .insert({
          sender_id: currentUserId,
          receiver_id: receiverId,
          status: 'pending'
        });

      if (insertError) {
        // Check if it's a limit error
        if (insertError.message.includes('friend limit')) {
          notify(`You have reached the maximum of ${MAX_FRIENDS} friends`, 'error');
        } else {
          throw insertError;
        }
        setLoading(false);
        return;
      }

      notify('Friend request sent!', 'success');
      setFriendId('');
      onSuccess();
      onClose();
    } catch (error) {
      notify('Failed to send friend request', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleQRScanSuccess = (scannedFriendId) => {
    setMode('input');
    setFriendId(scannedFriendId);
    handleSendRequest(scannedFriendId);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleSendRequest(friendId);
  };

  return (
    <Modal title="Add Friend" isOpen={isOpen} onClose={onClose} maxWidth="max-w-lg">
      {mode === 'input' ? (
        <div className="p-6">
          <h2 className="text-2xl font-bold text-brand-text mb-4">Add Friend</h2>
          
          {friendCount !== null && (
            <div className="mb-4 p-3 bg-gray-100 rounded-lg">
              <p className="text-sm text-content-primary">
                Friends: <span className="font-bold">{friendCount} / {MAX_FRIENDS}</span>
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-content-primary mb-2">
                Friend ID (8 characters)
              </label>
              <FcInput aria-label="ABC12345"
                type="text"
                value={friendId}
                onChange={(e) => setFriendId(e.target.value.toUpperCase())}
                placeholder="ABC12345"
                maxLength={8}
                className="w-full border focus:border-transparent"
                required
              />
              <p className="text-xs text-content-secondary mt-1">
                Enter your friend's 8-character Friend ID
              </p>
            </div>

            <div className="flex gap-2">
              <FcButton variant="primary" aria-label="Close"
                type="submit"
                disabled={loading || friendId.length !== 8}
                className="flex-1 disabled:cursor-not-allowed"
              >
                {loading ? 'Sending...' : 'Send Request'}
              </FcButton>
              <FcButton aria-pressed={mode === 'scan'} variant="tertiary"
                type="button"
                onClick={() => setMode('scan')}
                className="flex-1"
              >
                📷 Scan QR Code
              </FcButton>
            </div>
          </form>

          <FcButton variant="tertiary" type="submit"
            onClick={onClose}
            className="mt-4 w-full"
          >
            Cancel
          </FcButton>
        </div>
      ) : (
        <QRCodeScanner 
          onScanSuccess={handleQRScanSuccess}
          onClose={() => setMode('input')}
        />
      )}
    </Modal>
  );
}
